export const PRODUCT_NAME = 'Spawnwire';

export const LIMITS = {
  messageLength: 4000,
  /** Characters of a message kept for the conversation list's preview. */
  messagePreview: 200,
  attachmentsPerMessage: 10,
  groupDmSize: 10,
  pinsPerChannel: 50,
  reactionsPerMessage: 20,
  messagePageSize: 50,
  maxMessagePageSize: 100,
  spacesPerUser: 100,
  channelsPerSpace: 250,
  rolesPerSpace: 100,
  embedsPerMessage: 3,
  usernameMin: 2,
  usernameMax: 32,
  passwordMin: 8,
  passwordMax: 128,
  maxTimeoutDays: 28,
  forumTags: 20,
  tagsPerPost: 5,
} as const;

/** Slow mode choices, in seconds (0 is off). */
export const SLOWMODE_OPTIONS = [0, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 21600] as const;

/** Channel types people can post messages in directly (forum and media channels take posts instead). */
export const MESSAGE_CHANNEL_TYPES = ['text', 'announcement', 'thread', 'dm', 'group_dm'] as const;

// ---- Spaces and Discover ---------------------------------------------------------

/** A friends space is a private hangout; a community can pick a category and apply to be listed on Discover. */
export const SPACE_KINDS = ['friends', 'community'] as const;
export type SpaceKind = (typeof SPACE_KINDS)[number];

/** What a community is about. */
export const COMMUNITY_CATEGORIES = ['gaming', 'entertainment', 'science_tech', 'ai', 'chill', 'education'] as const;
export type CommunityCategory = (typeof COMMUNITY_CATEGORIES)[number];

export const COMMUNITY_CATEGORY_LABELS: Record<CommunityCategory, string> = {
  gaming: 'Gaming',
  entertainment: 'Entertainment',
  science_tech: 'Science & Tech',
  ai: 'AI',
  chill: 'Chill',
  education: 'Education',
};

/**
 * Space tags: a community can set a short tag that its members may wear next to their name. 1 to 4 characters:
 * letters, digits and a few symbols, with at least one letter or digit. ASCII only, so a tag can't impersonate
 * another with look-alike letters, and case is kept as typed.
 */
export const SPACE_TAG_MAX = 4;
export const SPACE_TAG_PATTERN = /^(?=.*[A-Za-z0-9])[A-Za-z0-9!#$%&*+\-./\\?@^_~]{1,4}$/;

/** The little icon shown in a space tag's chip. */
export const TAG_BADGES = ['bolt', 'star', 'heart', 'flame', 'leaf', 'gem', 'crown', 'shield', 'moon', 'music', 'gamepad', 'skull'] as const;
export type TagBadge = (typeof TAG_BADGES)[number];

/**
 * Where a space is with Discover: never applied (or withdrawn), waiting for staff review, listed, or turned down.
 * Only `approved` spaces are ever shown on Discover.
 */
export const DISCOVERY_STATUSES = ['none', 'pending', 'approved', 'denied'] as const;
export type DiscoveryStatus = (typeof DISCOVERY_STATUSES)[number];

/** Usernames nobody can register or take, because Spawnwire's own system account uses them. */
export const RESERVED_USERNAMES = ['spawnwire', 'spawnwire.system'] as const;

/** Fonts a user can pick for their display name. `null` on a user means the default font. */
export const NAME_FONTS = ['pixel', 'arcade', 'handwritten', 'elegant', 'bubbly', 'gothic', 'scifi', 'spooky', 'chunky'] as const;
export type NameFont = (typeof NAME_FONTS)[number];

// ---- Platform moderation -------------------------------------------------------

/** Spawnwire staff. Moderators handle day-to-day moderation; admins can do everything. */
export const STAFF_ROLES = ['moderator', 'admin'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

/** The rule an action was taken under; shown to the person in their account standing. */
export const MODERATION_RULES = ['spam', 'harassment', 'hate', 'violence', 'self_harm', 'sexual', 'scam', 'cheating', 'impersonation', 'server_abuse', 'other'] as const;
export type ModerationRule = (typeof MODERATION_RULES)[number];

/** Parts of Spawnwire an account can be blocked from without a full ban. */
export const RESTRICTIONS = ['messaging', 'hosting'] as const;
export type Restriction = (typeof RESTRICTIONS)[number];

export const MODERATION = {
  /** How long a warning counts toward someone's standing. */
  warningActiveDays: 90,
  /** Active warnings (or a recent ban) at which an account is "at risk". */
  atRiskWarnings: 2,
  /** Longest ban or restriction a moderator can give; longer or permanent ones need an admin. */
  moderatorMaxHours: 7 * 24,
  maxHours: 365 * 24,
} as const;

// ---- Support tickets -------------------------------------------------------------

/** What a support ticket is about. */
export const SUPPORT_CATEGORIES = ['game_server', 'account', 'bug', 'safety', 'other'] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

export const SUPPORT_CATEGORY_LABELS: Record<SupportCategory, string> = {
  game_server: 'Game server',
  account: 'Account or sign-in',
  bug: 'Something is broken',
  safety: 'Safety or abuse',
  other: 'Something else',
};

/** Waiting for staff, waiting for the person (staff replied), or done. */
export const SUPPORT_STATUSES = ['open', 'answered', 'closed'] as const;
export type SupportTicketStatus = (typeof SUPPORT_STATUSES)[number];

export const SUPPORT = {
  messageMax: 4000,
  subjectMax: 120,
  /** Unclosed tickets an account can have at once. */
  openPerAccount: 5,
} as const;

/** Server software people can pick for a hosted Minecraft server. */
export const MINECRAFT_SOFTWARE = ['vanilla', 'paper'] as const;
export type MinecraftSoftware = (typeof MINECRAFT_SOFTWARE)[number];

/** Largest file the server file editor opens or saves. */
export const MINECRAFT_EDIT_MAX_BYTES = 1024 * 1024;

/**
 * Cleans a path inside a hosted server ("plugins//x/./y" → "/plugins/x/y"), or null when it tries to leave the
 * server folder or has characters a path should not.
 */
export function normalizeServerPath(input: string): string | null {
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(input)) return null;
  const parts = input.split('/').filter((p) => p !== '' && p !== '.');
  if (parts.some((p) => p === '..' || p.length > 255)) return null;
  return `/${parts.join('/')}`;
}

export const ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Compare two time-ordered (UUIDv7) ids. Lowercase hex strings sort chronologically. */
export function compareIds(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function isNewer(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a) return false;
  if (!b) return true;
  return a > b;
}

/** Milliseconds since epoch encoded in a UUIDv7. */
export function idTimestamp(id: string): number {
  return parseInt(id.slice(0, 8) + id.slice(9, 13), 16);
}
